import * as signalR from "@microsoft/signalr";
import ApiService from "./ApiService";

type MessageStatus = 'Pending' | 'Started' | 'InProgress' | 'MessageUpdate' | 'Completed' | 'Failed';

export interface SignalRHandlers {
    onMessage: (message: { ThreadId: string; Content: string; Status: MessageStatus }) => void;
    onTitleChange: (titleUpdate: { ThreadId: string; NewTitle: string }) => void;
    onConnectionStatusChange: (status: 'Disconnected' | 'Connecting' | 'Connected') => void;
    onError: (error: any) => void;
}

export class SignalRService {
    private connection: signalR.HubConnection | null = null;
    private handlers: SignalRHandlers | null = null;
    private status: 'Disconnected' | 'Connecting' | 'Connected' = 'Disconnected';

    public get isConnected(): boolean {
        return this.connection?.state === signalR.HubConnectionState.Connected;
    }

    public async connect(negotiationUrl: string, hubName: string, handlers: SignalRHandlers, signal?: AbortSignal) {        
        if (import.meta.env.DEV) console.log('Attempting to connect to SignalR...');
        this.handlers = handlers;
        if (this.connection || this.isConnected || this.status === 'Connecting') {            
            if (import.meta.env.DEV) console.warn('SignalR connection already exists.');
            return;
        }
        this.updateConnectionStatus('Connecting');

        try {
            const { url, accessToken } = await this.negotiateConnection(negotiationUrl, hubName, signal);
            if (import.meta.env.DEV) console.log('Negotiation successful:', url);
            this.connection = new signalR.HubConnectionBuilder()
                .withUrl(url, { accessTokenFactory: () => accessToken })
                .withAutomaticReconnect()
                .configureLogging(signalR.LogLevel.Information)
                .build();

            this.registerHandlers();

            if (import.meta.env.DEV) console.log('Starting SignalR connection...');
            await this.connection.start();
            this.updateConnectionStatus('Connected');
            if (import.meta.env.DEV) console.log('SignalR Connected');
        } catch (err) {
            console.error('SignalR Connection Error: ', err);
            this.updateConnectionStatus('Disconnected');
            this.handlers?.onError(err);
            setTimeout(() => this.connect(negotiationUrl, hubName, handlers, signal), 5000);
        }
    }

    private registerHandlers() {
        this.connection?.on('MessageChunk', (message: { ThreadId: string; Content: string; Status: MessageStatus }) => {
            if (import.meta.env.DEV) console.log('Message from SignalR:', message);
            this.handlers?.onMessage(message);
        });

        this.connection?.on('TitleChange', (titleUpdate: { ThreadId: string; NewTitle: string }) => {
            if (import.meta.env.DEV) console.log('Title update from SignalR:', titleUpdate);
            this.handlers?.onTitleChange(titleUpdate);
        });

        this.connection?.onclose(() => {
            if (import.meta.env.DEV) console.log('SignalR Disconnected');
            this.updateConnectionStatus('Disconnected');
        });
    }

    private removeHandlers() {
        this.connection?.off('MessageChunk');
        this.connection?.off('TitleChange');
    }

    private async negotiateConnection(negotiationUrl: string, hubName: string, signal?: AbortSignal): Promise<{ url: string; accessToken: string }> {
        if (import.meta.env.DEV) console.log('Negotiating connection with', negotiationUrl, hubName);
        const response = await ApiService.post({
            url: negotiationUrl,
            data: { hub: hubName },
            signal
        });
        if (import.meta.env.DEV) console.log('Negotiation response received');
        const data = response as { url: string; accessToken: string };
        return {
            url: data.url,
            accessToken: data.accessToken
        };
    }

    private updateConnectionStatus(status: 'Disconnected' | 'Connecting' | 'Connected') {
        this.status = status;
        this.handlers?.onConnectionStatusChange(status);
    }

    public async disconnect() {
        if (this.connection) {
            await this.connection.stop();
            this.removeHandlers();
            this.connection = null;
            this.updateConnectionStatus('Disconnected');
            if (import.meta.env.DEV) console.log('SignalR connection stopped.');
        }
    }
}

//export const signalRService = new SignalRService();